import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

import { AlertCircle, CreditCard } from "lucide-react";
import LoadingSpinner from "../animations/LoadingSpinner";

const StripePaymentForm = ({
  clientSecret,
  paymentDetails,
  onPaymentSuccess,
  onPaymentError,
  isProcessing,
  disabled = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret || isProcessing || disabled) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Confirm the payment with Stripe
      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
          },
        });

      if (confirmError) {
        setError(confirmError.message);
        onPaymentError?.(confirmError);
      } else if (paymentIntent.status === "succeeded") {
        onPaymentSuccess?.(paymentIntent);
      } else {
        setError("Payment was not successful. Please try again.");
        onPaymentError?.(new Error("Payment not successful"));
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
      onPaymentError?.(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#424770",
        "::placeholder": {
          color: "#aab7c4",
        },
        fontFamily: "system-ui, -apple-system, sans-serif",
      },
      invalid: {
        color: "#9e2146",
      },
    },
    hidePostalCode: false,
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CreditCard className="h-5 w-5 text-[#0052FD]" />
          Payment Information
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Payment Summary */}
        {paymentDetails && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Consultation Fee:</span>
              <span className="font-medium">
                ${paymentDetails.consultationFee?.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Platform Fee:</span>
              <span className="font-medium">
                ${paymentDetails.platformFee?.toFixed(2)}
              </span>
            </div>
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total Amount:</span>
              <span className="text-[#0052FD]">
                ${paymentDetails.totalAmount?.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Card Element */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="border rounded-lg p-4 bg-white">
            <CardElement
              options={cardElementOptions}
              onChange={(event) => {
                if (event.error) {
                  setError(event.error.message);
                } else {
                  setError(null);
                }
              }}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={
              !stripe ||
              !clientSecret ||
              isSubmitting ||
              isProcessing ||
              disabled
            }
            className="w-full bg-[#0052FD] hover:bg-[#0052FD]/90 text-white"
            size="lg"
          >
            {isSubmitting || isProcessing ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Processing Payment...
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Pay ${paymentDetails?.totalAmount?.toFixed(2) || "0.00"}
              </>
            )}
          </Button>
        </form>

        {/* Security Notice */}
        <div className="text-xs text-gray-500 text-center">
          <p>Your payment information is secure and encrypted.</p>
          <p>Powered by Stripe • Payment processed by platform</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default StripePaymentForm;
